import React, { useState } from 'react';
import { Clapperboard, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const PRESETS = [
  { value: 24, label: "24 fps", hint: "Cinema standard" },
  { value: 25, label: "25 fps", hint: "PAL / Europe TV" },
  { value: 30, label: "30 fps", hint: "NTSC / US TV" },
];

export default function DavinciExportModal({ open, onOpenChange, onConfirm }) {
  const [mode, setMode] = useState('preset');
  const [preset, setPreset] = useState(24);
  const [custom, setCustom] = useState('');

  const customNum = parseFloat(custom);
  const customValid = !isNaN(customNum) && customNum > 0 && customNum <= 240;
  const canConfirm = mode === 'preset' || customValid;

  const handleConfirm = () => {
    const rate = mode === 'preset' ? preset : customNum;
    onConfirm(rate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clapperboard className="w-4 h-4 text-primary" />
            Export Logiciel
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
              Framerate de la timeline
            </Label>

            <div className="grid grid-cols-3 gap-2 mb-3">
              {PRESETS.map((p) => {
                const selected = mode === 'preset' && preset === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { setMode('preset'); setPreset(p.value); }}
                    data-testid={`framerate-${p.value}`}
                    className={
                      "flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition-colors " +
                      (selected
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40 hover:bg-secondary/50")
                    }
                  >
                    <span className="text-sm font-semibold">{p.label}</span>
                    <span className="text-[10px] text-muted-foreground">{p.hint}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setMode('custom')}
              className={
                "w-full text-left rounded-lg border p-3 transition-colors " +
                (mode === 'custom'
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/40 hover:bg-secondary/50")
              }
              data-testid="framerate-custom-toggle"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Personnalise</div>
                  <div className="text-[10px] text-muted-foreground">23.976, 29.97, 48, 60...</div>
                </div>
                {mode === 'custom' && (
                  <Input
                    type="number"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="fps"
                    min="1"
                    max="240"
                    step="0.001"
                    className="w-24 h-8 text-sm"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="framerate-custom-input"
                    autoFocus
                  />
                )}
              </div>
            </button>

            {mode === 'custom' && custom && !customValid && (
              <p className="text-[11px] text-destructive mt-2">
                Entre une valeur entre 1 et 240.
              </p>
            )}
          </div>

          <div className="text-[11px] text-muted-foreground border-t border-border pt-3">
            Le ZIP contient <code className="text-foreground">ExportLogiciel.fcpxml</code>, un dossier
            <code className="text-foreground"> Media/</code> avec les images et un <code className="text-foreground">README.md</code> d'instructions.
            Décompresse l'archive puis importe le <code className="text-foreground">.fcpxml</code> dans ton logiciel de montage.
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={!canConfirm} data-testid="davinci-export-confirm">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Exporter
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
